import { authURL } from '@/composables/useRuntimeConfig'
import { clearAuthCapability } from './transport'
import { readCsrfCookie } from './csrf'

/**
 * /auth/me bootstrap endpoint. Not exposed via Connect because it's called
 * before the app has a transport (circular dependency on the auth guard).
 */
export interface MeUser {
  id: string
  username: string
  email: string
  display_name?: string
  avatar_url?: string
  // Set when the user has an in-flight DeleteAccountWorkflow during the
  // 10-day grace period. SPA renders a global banner offering "cancel
  // deletion" until this clears (cancel) or the user is soft-deleted.
  pending_deletion_at?: string
}

export interface MeOrganization {
  id: string
  name: string
  display_name?: string
  linked_user_id?: string
}

export interface MeMembership {
  organization: MeOrganization
  role: string
  joined_at: string
  member_count: number
}

export interface MeResponse {
  user: MeUser | null
  memberships: MeMembership[]
  session_expiry?: string
  // Server-computed from the admin allowlist; the list itself never
  // leaves the server. Gates the Admin link only.
  can_access_admin?: boolean
}

// fresh skips the one-minute server cache of the stored profile. Use it
// after a change to the profile or to the account deletion schedule.
export async function fetchMe(fresh = false): Promise<MeResponse | null> {
  const res = await fetch(authURL(fresh ? '/auth/me?fresh=1' : '/auth/me'), {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (res.status === 401) return null
  if (!res.ok) throw new Error(`GET /auth/me: ${res.status}`)
  const body = (await res.json()) as MeResponse
  if (body.user === null) return null
  return body
}

export type RefreshSessionResult = 'refreshed' | 'invalid' | 'unavailable'

export async function refreshSession(): Promise<RefreshSessionResult> {
  const csrf = readCsrfCookie()
  const res = await fetch(authURL('/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
    ...(csrf ? { headers: { 'X-CSRF-Token': csrf } } : {}),
  })
  if (res.ok) {
    clearAuthCapability()
    return 'refreshed'
  }
  if (res.status >= 500) return 'unavailable'
  return 'invalid'
}

export async function logout(): Promise<void> {
  await fetch(authURL('/auth/logout'), {
    method: 'POST',
    credentials: 'include',
  })
}
